import axios from 'axios';

// Backend URL resolution.
// 1) Standard path: use REACT_APP_BACKEND_URL baked at build time.
// 2) Self-host fallback: when the app is served from any *.emergent.host
//    deployment URL, the backend is co-hosted on the SAME origin. The
//    Emergent build pipeline may bake a stale/canonical backend URL that
//    no longer routes (e.g., HTTP 400 "Deployment not found"). To make the
//    frontend resilient we prefer window.location.origin in that case so
//    requests stay on the same domain that successfully served this bundle.
const BUILD_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const RESOLVED_BACKEND_URL = (() => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    if (host.endsWith('.emergent.host')) return window.location.origin;
  }
  return BUILD_BACKEND_URL;
})();

const API_URL = `${RESOLVED_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('rf_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // iter 179 — Tame the 401 interceptor so transient missing-header
    // races (page reload before localStorage hydrates, SSE reconnect
    // dropping Authorization, worker restart) do NOT wipe the user's
    // session and force a /login redirect.
    //
    // Only logout when the backend explicitly tells us the token is
    // gone or the account is invalid. iter178's `Not authenticated`
    // detail (missing header) is treated as transient and is NOT a
    // logout signal — it's almost always a race.
    //
    // Logout triggers: token revoked, token expired, invalid token,
    // user not found, or user deactivated.
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail || '';
      const isHardAuthFailure = [
        'Token has been revoked',
        'Token expired',
        'Invalid token',
        'User not found',
        'User is deactivated',
        'Invalid user',
      ].some((m) => detail === m);

      if (isHardAuthFailure && localStorage.getItem('rf_token')) {
        localStorage.removeItem('rf_token');
        window.location.href = '/login';
      }
      // Soft 401 (e.g., "Not authenticated" — missing header race):
      // bubble the error up to the caller. The page typically retries
      // on its next polling tick once the token has been re-attached
      // by the request interceptor.
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Invites
export const inviteAPI = {
  create: (data) => api.post('/invites', data),
  accept: (data) => api.post('/invites/accept', data),
  list: () => api.get('/invites'),
  generate: (data) => api.post('/invites/generate', data),
  validate: (token) => api.get(`/invites/validate/${token}`),
  // Manual invite - creates user directly with temp password (Admin only)
  manualInvite: (data) => api.post('/admin/invite-user', data),
  // Delete user (Admin only)
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  // Reset user password (Admin only)
  resetPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  // Activate user (Admin only)
  activateUser: (userId) => api.put(`/admin/users/${userId}/activate`),
  // Deactivate user (Admin only)
  deactivateUser: (userId) => api.put(`/admin/users/${userId}/deactivate`),
};

// Teams
export const teamAPI = {
  create: (data) => api.post('/teams', data),
  list: () => api.get('/teams'),
  get: (id) => api.get(`/teams/${id}`),
  getMembers: (id) => api.get(`/teams/${id}/members`),
  assignUser: (teamId, userId) => api.put(`/teams/${teamId}/assign?user_id=${userId}`),
  // iter 168 — Multi-team recruiter membership
  getRecruiters: (id) => api.get(`/teams/${id}/recruiters`),
  addRecruiters: (id, userIds) => api.post(`/teams/${id}/recruiters`, { user_ids: userIds }),
  removeRecruiter: (id, userId) => api.delete(`/teams/${id}/recruiters/${userId}`),
  availableRecruiters: () => api.get('/recruiters/available'),
};

// Users
export const userAPI = {
  list: (params) => api.get('/users', { params }),
  deactivate: (id) => api.put(`/users/${id}/deactivate`),
  activate: (id) => api.put(`/users/${id}/activate`),
  // iter 181 — Admin User Management edits
  adminUpdateProfile: (id, data) => api.put(`/users/${id}/profile`, data),
  adminUpdateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  // NEW: Team Leads with open positions for BDE job creation
  getTeamLeads: () => api.get('/users/team-leads'),
  getIcfUsers: () => api.get('/users/icf-users'),
  // NEW: Recruiters in team for Team Lead job assignment
  getRecruitersInTeam: (teamLeadId) => api.get(`/users/recruiters-in-team/${teamLeadId}`),
  // iter 103: Live team-scoped recruiter list for the caller's role (BDEM/BDE/TL/Admin)
  getRecruitersInMyTeam: () => api.get('/users/recruiters-in-my-team'),
  // NEW: Get managed BDEs for BDE Manager
  getMyManagedBdes: () => api.get('/users/my-managed-bdes'),
  // NEW: Get assigned recruiters for BDE
  getMyAssignedRecruiters: () => api.get('/users/my-assigned-recruiters'),
  // iter 152: Get Recruiter Managers available to the caller for job creation
  // dropdown. BDE/BDEM → team RMs; Admin → all RMs in the org.
  getMyRecruiterManagers: () => api.get('/users/my-recruiter-managers'),
  // iter 159 — TLs available to BDE / BDE-Manager for the new "Assign to
  // Team Lead" picker on the create-job form.
  getMyTeamLeads: () => api.get('/users/my-team-leads'),
};

// Permissions & RBAC
export const permissionAPI = {
  getMy: () => api.get('/permissions/my'),
  getRoles: () => api.get('/permissions/roles'),
  getUser: (userId) => api.get(`/permissions/user/${userId}`),
  updateUser: (userId, data) => api.put(`/permissions/user/${userId}`, data),
  resetUser: (userId) => api.delete(`/permissions/user/${userId}`),
};

// Audit Logs
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  summary: (days = 7) => api.get('/audit-logs/summary', { params: { days } }),
  dimensions: () => api.get('/audit-logs/dimensions'),
};

// Admin Dashboard
export const adminAPI = {
  dashboardStats: () => api.get('/admin/dashboard-stats'),
  // iter 177 — Admin Pool Health Widget data source. Returns CPU +
  // Gmail isolation metrics, scheduler leader status, and a roll-up
  // `status ∈ {healthy, warning, critical}`. Polled every 15s on the
  // Admin dashboard.
  systemHealth: () => api.get('/admin/system-health'),
};

// Clients
export const clientAPI = {
  create: (data) => api.post('/clients', data),
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  update: (id, data) => api.put(`/clients/${id}`, data),
  canEdit: (id) => api.get(`/clients/${id}/can-edit`),
  // iter 120 — Delete client (blocked when jobs reference it)
  deletePreview: (id) => api.get(`/clients/${id}/delete-preview`),
  delete: (id) => api.delete(`/clients/${id}`),
  // iter 201 — Close / Reopen client lifecycle
  close: (id) => api.post(`/clients/${id}/close`),
  reopen: (id) => api.post(`/clients/${id}/reopen`),
};

// Jobs
export const jobAPI = {
  create: (data) => api.post('/jobs', data),
  list: (params) => api.get('/jobs', { params }),
  get: (id) => api.get(`/jobs/${id}`),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  canEdit: (id) => api.get(`/jobs/${id}/can-edit`),
  assignRecruiter: (jobId, recruiterId) => api.put(`/jobs/${jobId}/assign-recruiter?recruiter_id=${recruiterId}`),
  // iter 117 — Manage Assignments (multi-TL + multi-Recruiter, team-scoped)
  getAssignableUsers: (jobId) => api.get(`/jobs/${jobId}/assignable-users`),
  // iter 120 — Hard-delete Job with confirmation preview
  deletePreview: (jobId) => api.get(`/jobs/${jobId}/delete-preview`),
  delete: (jobId) => api.delete(`/jobs/${jobId}`),
  updateStatus: (jobId, status) => api.put(`/jobs/${jobId}/status?status=${status}`),
  // AI Ranked Candidates
  getRankedCandidates: (jobId, limit = 20) => api.get(`/jobs/${jobId}/ranked-candidates`, { params: { limit } }),
  // BDE shortlisted candidates (limited view)
  getShortlistedCandidates: (jobId) => api.get(`/jobs/${jobId}/shortlisted-candidates`),
  // Team Lead shortlisted candidates (with status update capability)
  getTeamLeadShortlistedCandidates: (jobId) => api.get(`/teamlead/job/${jobId}/shortlisted-candidates`),
  // Recruiter job candidates (for job detail page with ownership)
  getRecruiterJobCandidates: (jobId) => api.get(`/recruiter/job/${jobId}/candidates`),
  // Smart Match Suggestions (Recruiter/TL only) - returns candidates ranked by match score
  getSmartMatches: (jobId, limit = 20) => api.get(`/jobs/${jobId}/smart-matches`, { params: { limit } }),
  // BDE candidate details (read-only)
  getBdeCandidateDetails: (candidateId, jobId) => api.get(`/bde/candidate/${candidateId}/details`, { params: { job_id: jobId } }),
};

// Candidates
export const candidateAPI = {
  create: (data) => api.post('/candidates', data),
  // iter269 — was missing here (only on smartMatchAPI) → AssignToJobDialog threw
  // a TypeError and every AI Search "Assign to Job" failed with a generic toast.
  assignToJob: (candidateId, jobId, formData) => api.post(
    `/candidates/${candidateId}/assign-to-job?job_id=${jobId}`,
    formData || undefined,
    formData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined),
  list: (params) => api.get('/candidates', { params }),
  count: (params) => api.get('/candidates/count', { params }),
  aiRerank: (data) => api.post('/candidates/ai-rerank', data),
  get: (id) => api.get(`/candidates/${id}`),
  update: (id, data) => api.put(`/candidates/${id}`, data),
  delete: (id) => api.delete(`/candidates/${id}`),
  bulkDelete: (candidateIds) => api.post('/candidates/bulk-delete', { candidate_ids: candidateIds }),
  updateStatus: (id, data) => api.put(`/candidates/${id}/status`, data),
  assign: (id, recruiterId) => api.put(`/candidates/${id}/assign?recruiter_id=${recruiterId}`),
  // iter 96: notification helpers exposed on candidateAPI for convenience
  markNotificationPageRead: (page) => api.put(`/notifications/mark-page-read?page=${encodeURIComponent(page)}`),
  notificationsSummary: () => api.get('/notifications/summary'),
  // Score endpoint
  score: (id) => api.post(`/candidates/${id}/score`),
  // Resume endpoints
  hasResume: (candidateId) => api.get(`/candidates/${candidateId}/has-resume`),
  downloadResume: (candidateId) => api.get(`/candidates/${candidateId}/resume`, { responseType: 'blob' }),
  viewResume: (fileId) => api.get(`/resumes/${fileId}/view`, { responseType: 'blob' }),
  // iter217 — token-authenticated URL for opening the stored resume in a NEW TAB
  // (browser-native streaming: correct content-type, no blob-modal loading state)
  resumeViewUrl: (fileId) =>
    `${API_URL}/resumes/${fileId}/view?token=${encodeURIComponent(localStorage.getItem('rf_token') || '')}`,
  // iter243 — central resume VIEWER page for new-tab opens: shows the original
  // filename, an always-available Download button, and the inline preview.
  // Download stays available even when the preview cannot render.
  resumeViewerUrl: (fileId) =>
    `${API_URL}/resumes/${fileId}/viewer?token=${encodeURIComponent(localStorage.getItem('rf_token') || '')}`,
  getDocuments: (candidateId) => api.get(`/candidates/${candidateId}/documents`),
  // Team Lead status update
  updateStatusTeamLead: (id, status) => api.put(`/teamlead/candidates/${id}/status?status=${status}`),
  // HR Approval Workflow
  hrApprove: (id) => api.put(`/candidates/${id}/hr-approve`),
  requestFinalApproval: (id) => api.put(`/candidates/${id}/request-final-approval`),
  finalApprove: (id) => api.put(`/candidates/${id}/final-approve`),
  rejectApproval: (id, reason = '') => api.put(`/candidates/${id}/reject-approval?reason=${encodeURIComponent(reason)}`),
  bulkUpload: (formData, jobId) => {
    const url = jobId ? `/candidates/bulk-upload?job_opening_id=${jobId}` : '/candidates/bulk-upload';
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadResume: (candidateId, formData) => api.post(`/candidates/${candidateId}/upload-resume`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // iter 199 — Turing RTR (.eml)
  uploadRTR: (candidateId, formData) => api.post(`/candidates/${candidateId}/upload-rtr`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadRTR: (candidateId) => api.get(`/candidates/${candidateId}/rtr`, { responseType: 'blob' }),
  getRTRParsed: (candidateId) => api.get(`/candidates/${candidateId}/rtr/parsed`),
  downloadRTRAttachment: (candidateId, index) => api.get(`/candidates/${candidateId}/rtr/attachment/${index}`, { responseType: 'blob' }),
};

// Interviews
export const interviewAPI = {
  create: (data) => api.post('/interviews', data),
  list: (params) => api.get('/interviews', { params }),
  get: (id) => api.get(`/interviews/${id}`),
  update: (id, data) => api.put(`/interviews/${id}`, null, { params: data }),
  updateFeedback: (id, data) => api.put(`/interviews/${id}/feedback`, data),
  addFeedback: (id, data) => api.put(`/interviews/${id}/feedback`, data),
  sendReminder: (id, type) => api.post(`/interviews/${id}/send-reminder?reminder_type=${type}`),
  getPendingReminders: () => api.get('/interviews/pending-reminders'),
};

// Attendance
export const attendanceAPI = {
  checkIn: (data) => api.post('/attendance/check-in', data),
  list: (params) => api.get('/attendance', { params }),
};

// Settings
export const settingsAPI = {
  getGeofence: () => api.get('/settings/geofence'),
  setGeofence: (data) => api.post('/settings/geofence', data),
};

// Analytics
export const analyticsAPI = {
  dashboard: () => api.get('/analytics/dashboard'),
  performance: (params) => api.get('/analytics/performance', { params }),
  pipeline: (params) => api.get('/analytics/pipeline', { params }),
};

// Recruiter Credits (RECRUITER ROLE ONLY)
export const recruiterCreditsAPI = {
  getMyCredits: () => api.get('/recruiter-credits/my-credits'),
  getMyActivity: (limit = 20) => api.get('/recruiter-credits/my-activity', { params: { limit } }),
  getLeaderboard: (limit = 10) => api.get('/recruiter-credits/leaderboard', { params: { limit } }),
};

// Role-Based Dashboards
export const dashboardAPI = {
  bde: () => api.get('/dashboard/bde'),
  recruiter: () => api.get('/dashboard/recruiter'),
  recruiterPipeline: () => api.get('/dashboard/recruiter/pipeline'),
  admin: () => api.get('/dashboard/admin'),
  teamLead: () => api.get('/dashboard/team-lead'),
  teamLeadRecruiterTable: (params = {}) => api.get('/dashboard/team-lead/recruiter-table', { params }),
  teamLeadRequirementsTable: (params = {}) => api.get('/dashboard/team-lead/requirements-table', { params }),
  hr: () => api.get('/dashboard/hr'),
  bdeManager: () => api.get('/dashboard/bde-manager'),
  recruiterLeaderboard: () => api.get('/dashboard/recruiter-leaderboard'),
  // iter 102: real-time top-line snapshot for Admin/BDEM/TL TeamSnapshot cards
  // iter 104: accepts optional {date_from, date_to} (ISO YYYY-MM-DD)
  adminSnapshot: (params = {}) => api.get('/dashboard/admin-snapshot', { params }),
};

// Team Lead - My team + per-recruiter metrics
export const teamLeadAPI = {
  myTeam: () => api.get('/teamlead/my-team'),
};

// BDE Manager Detailed Views (READ-ONLY)
export const bdeManagerAPI = {
  getBdeDetails: (bdeId) => api.get(`/bde-manager/bde/${bdeId}/details`),
  getAllJobs: (params = {}) => api.get('/bde-manager/jobs', { params }),
  getJobDetails: (jobId) => api.get(`/bde-manager/jobs/${jobId}/details`),
  getAllClients: (params = {}) => api.get('/bde-manager/clients', { params }),
  getClientJobs: (clientId) => api.get(`/bde-manager/clients/${clientId}/jobs`),
};

// Notifications
export const notificationAPI = {
  list: (limit = 20) => api.get('/notifications', { params: { limit } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Skills Autocomplete
export const skillsAPI = {
  suggest: (query) => api.get('/skills/suggest', { params: { query } }),
};

// Google Drive
export const driveAPI = {
  connect: () => {
    // Pass current origin to backend for dynamic redirect URI
    const redirectUri = `${window.location.origin}/api/oauth/drive/callback`;
    return api.get('/oauth/drive/connect', { params: { redirect_uri: redirectUri } });
  },
  status: () => api.get('/oauth/drive/status'),
  disconnect: () => api.post('/oauth/drive/disconnect'),
  listResumes: () => api.get('/drive/resumes'),
  listFiles: (folderId) => api.get('/drive/files', { params: { folder_id: folderId } }),
};

// Gmail
export const gmailAPI = {
  // OAuth
  connect: () => {
    // Pass current origin to backend for dynamic redirect URI
    const redirectUri = `${window.location.origin}/api/oauth/gmail/callback`;
    return api.get('/oauth/gmail/connect', { params: { redirect_uri: redirectUri } });
  },
  getStatus: () => api.get('/oauth/gmail/status'),
  status: () => api.get('/oauth/gmail/status'),
  disconnect: () => api.post('/oauth/gmail/disconnect'),
  
  // Send emails
  send: (data) => api.post('/email/send', data),
  sendTemplate: (data) => api.post('/email/send-template', data),
  templates: () => api.get('/email/templates'),
  logs: (limit = 50) => api.get('/email/logs', { params: { limit } }),
  
  // Inbox
  listInbox: (params = {}) => api.get('/gmail/inbox', { params }),
  getMessage: (messageId) => api.get(`/gmail/message/${messageId}`),
  getAttachment: (messageId, attachmentId) => api.get(`/gmail/attachment/${messageId}/${attachmentId}`, { responseType: 'blob' }),
  
  // Processing
  processEmail: (messageId, assignToRecruiterId = null) => api.post('/gmail/process-email', { 
    message_id: messageId, 
    assign_to_recruiter_id: assignToRecruiterId 
  }),
  batchProcess: (messageIds, assignToRecruiterId = null) => api.post('/gmail/batch-process', messageIds, {
    params: { assign_to_recruiter_id: assignToRecruiterId }
  }),
  markProcessed: (messageId) => api.post(`/gmail/mark-processed?message_id=${messageId}`),
  
  // Configuration
  getPollingConfig: () => api.get('/gmail/polling-config'),
  updatePollingConfig: (config) => api.put('/gmail/polling-config', config),
  getUnprocessedCount: () => api.get('/gmail/unprocessed-count'),
  
  // Processing logs
  getProcessingLogs: (params = {}) => api.get('/email/processing-logs', { params }),
  
  // NEW: Automation endpoints
  syncNow: () => api.post('/gmail/sync-now'),
  getSyncStatus: () => api.get('/gmail/sync-status'),
};

// Gmail Sync (Automated Background Sync)
export const gmailSyncAPI = {
  // User endpoints
  getStatus: () => api.get('/gmail-sync/status'),
  startBackfill: (config = {}) => api.post('/gmail-sync/start-backfill', config),
  pause: () => api.post('/gmail-sync/pause'),
  resume: () => api.post('/gmail-sync/resume'),
  getRecentLogs: (limit = 20) => api.get('/gmail-sync/recent-logs', { params: { limit } }),
  
  // Admin endpoints
  getOrgStats: () => api.get('/gmail-sync/admin/stats'),
  getAllUserSyncs: (params = {}) => api.get('/gmail-sync/admin/users', { params }),
  adminAction: (action, userId) => api.post('/gmail-sync/admin/action', { action, user_id: userId }),
  getTimeseries: (metric, days = 30) => api.get('/gmail-sync/admin/metrics/timeseries', { params: { metric, days } }),
  getReviewQueue: (params = {}) => api.get('/gmail-sync/admin/review-queue', { params }),
  getTopDomains: (limit = 10) => api.get('/gmail-sync/admin/top-domains', { params: { limit } }),
  
  // Auto-sync endpoints (NEW)
  getAutoSyncConfig: () => api.get('/gmail/auto-sync/config'),
  updateAutoSyncConfig: (config) => api.put('/gmail/auto-sync/config', config),
  getAutoSyncStatus: () => api.get('/gmail/auto-sync/status'),
  triggerAutoSync: () => api.post('/gmail/auto-sync/run'),
  stopAutoSync: () => api.post('/gmail/auto-sync/stop'),
  getAutoSyncLogs: (limit = 50) => api.get('/gmail/auto-sync/logs', { params: { limit } }),
};

// Timesheets
export const timesheetAPI = {
  create: (data) => api.post('/timesheets', data),
  list: (params) => api.get('/timesheets', { params }),
  approve: (id) => api.put(`/timesheets/${id}/approve`),
  reject: (id) => api.put(`/timesheets/${id}/reject`),
};

// Workorders
export const workorderAPI = {
  create: (data) => api.post('/workorders', data),
  list: (params) => api.get('/workorders', { params }),
  get: (id) => api.get(`/workorders/${id}`),
  updateStatus: (id, status) => api.put(`/workorders/${id}/status?status=${status}`),
  pendingTasks: () => api.get('/workorders/pending-tasks'),
  skipTask: (taskId) => api.post(`/workorders/pending-tasks/${taskId}/skip`),
};

// Laptops
export const laptopAPI = {
  create: (data) => api.post('/laptops', data),
  list: (params) => api.get('/laptops', { params }),
  assign: (id, data) => api.post(`/laptops/${id}/assign`, data),
  return: (id, notes) => api.post(`/laptops/${id}/return`, null, { params: { notes } }),
  history: (id) => api.get(`/laptops/${id}/history`),
};

// Email Requests
export const emailRequestAPI = {
  create: (data) => api.post('/email-requests', data),
  list: (params) => api.get('/email-requests', { params }),
  process: (id, actualEmail, action) => api.put(`/email-requests/${id}/process`, null, { 
    params: { actual_email: actualEmail, action } 
  }),
};

// Messages
export const messageAPI = {
  send: (data) => api.post('/messages', data),
  list: (folder) => api.get('/messages', { params: { folder } }),
  markRead: (id) => api.put(`/messages/${id}/read`),
  unreadCount: () => api.get('/messages/unread-count'),
};

// Resume Matching & AI
export const resumeAPI = {
  parse: (file, jobOpeningId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (jobOpeningId) formData.append('job_opening_id', jobOpeningId);
    return api.post('/resume/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // iter 212 — background parse: request returns instantly with {job_id};
  // taskAPI.awaitResult polls the tiny task doc until the worker finishes.
  parseAsync: (file, jobOpeningId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (jobOpeningId) formData.append('job_opening_id', jobOpeningId);
    return api.post('/resume/parse-async', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // helper: parse on the background queue, resolve with the same response
  // shape as the legacy sync parse so callers don't change.
  parseInBackground: async (file, jobOpeningId = null) => {
    const start = await resumeAPI.parseAsync(file, jobOpeningId);
    const result = await taskAPI.awaitResult(start.data.job_id);
    return { data: result };
  },
  createCandidate: (file, jobOpeningId = null, skipDuplicate = true) => {
    const formData = new FormData();
    formData.append('file', file);
    if (jobOpeningId) formData.append('job_opening_id', jobOpeningId);
    formData.append('skip_duplicate', skipDuplicate);
    return api.post('/resume/create-candidate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  bulkUpload: (files, jobOpeningId = null, skipDuplicates = true) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (jobOpeningId) formData.append('job_opening_id', jobOpeningId);
    formData.append('skip_duplicates', skipDuplicates);
    return api.post('/resume/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getMatch: (candidateId, jobId) => api.get(`/resume/match/${candidateId}/${jobId}`),
  getRankedCandidates: (jobId, limit = 20) => api.get(`/jobs/${jobId}/ranked-candidates`, { params: { limit } }),
};

// Smart Matching & Candidate Assignment
// iter269 — Admin-managed custom pipeline statuses
export const pipelineStatusAPI = {
  list: () => api.get('/pipeline-statuses'),
  create: (data) => api.post('/pipeline-statuses', data),
  update: (id, data) => api.put(`/pipeline-statuses/${id}`, data),
  remove: (id, migrateTo) => api.delete(`/pipeline-statuses/${id}`,
    migrateTo ? { params: { migrate_to: migrateTo } } : undefined),
};

export const smartMatchAPI = {
  getMatches: (jobId, opts = {}) => api.get(`/jobs/${jobId}/smart-match`, {
    params: {
      page: opts.page || 1,
      page_size: opts.pageSize || 10,
      ...(opts.refresh ? { refresh: true } : {}),
    },
  }),
  shortlistFromSuggestion: (jobId, candidateId) => api.post(`/jobs/${jobId}/shortlist-from-suggestion?candidate_id=${candidateId}`),
  assignToJob: (candidateId, jobId, formData) => api.post(
    `/candidates/${candidateId}/assign-to-job?job_id=${jobId}`,
    formData || undefined,
    formData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined),
};

// Export API - CSV, Excel, PDF
// iter 211 — async background imports (CSV / ZIP / resume batches)
export const importAPI = {
  start: (files, jobId, urlColumn = null) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    if (jobId) fd.append('job_opening_id', jobId);
    if (urlColumn) fd.append('url_column', urlColumn);
    return api.post('/imports', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // iter 214 — chunked upload flow: large ZIPs (up to 2GB) never hit proxy/body limits
  uploadInit: (filename, size) => api.post('/imports/uploads/init', { filename, size }),
  uploadChunk: (uploadId, index, blob) => {
    const fd = new FormData();
    fd.append('chunk', blob, 'chunk.bin');
    return api.post(`/imports/uploads/${uploadId}/chunk?index=${index}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
  },
  startFromUploads: (uploadIds, jobId, urlColumn = null) =>
    api.post('/imports/start', { upload_ids: uploadIds, job_opening_id: jobId, url_column: urlColumn }),
  list: (params) => api.get('/imports', { params }),
  get: (id) => api.get(`/imports/${id}`),
  cancel: (id) => api.post(`/imports/${id}/cancel`),
};

// iter 211 — Inhouse Developers page (live view of the B2C developer pool)
export const inhouseAPI = {
  list: (params) => api.get('/inhouse-developers', { params }),
  matches: (jobId, params) => api.get(`/jobs/${jobId}/inhouse-developers`, { params }),
  resume: (devId) => api.get(`/inhouse-developers/${devId}/resume`, { responseType: 'blob' }),
  submit: (jobId, devId) => api.post(`/jobs/${jobId}/inhouse-developers/${devId}/submit`),
};

// iter 212 — persistent background task queue status
export const taskAPI = {
  get: (id) => api.get(`/tasks/${id}`),
  list: (params) => api.get('/tasks', { params }),
  awaitResult: async (jobId, { interval = 1500, timeout = 240000 } = {}) => {
    const t0 = Date.now();
    for (;;) {
      const { data } = await api.get(`/tasks/${jobId}`);
      if (data.status === 'completed') return data.result;
      if (data.status === 'failed') {
        const err = new Error(data.error || 'Processing failed');
        err.response = { data: { detail: data.error || 'Processing failed' } };
        throw err;
      }
      if (Date.now() - t0 > timeout) {
        const err = new Error('Processing timed out');
        err.response = { data: { detail: 'Processing timed out — please try again' } };
        throw err;
      }
      await new Promise((r) => setTimeout(r, interval));
    }
  },
};

export const exportAPI = {
  candidatesCSV: (params = {}) => api.get('/exports/candidates/csv', { params, responseType: 'blob' }),
  candidatesExcel: (params = {}) => api.get('/exports/candidates/excel', { params, responseType: 'blob' }),
  candidatesPDF: (params = {}) => api.get('/exports/candidates/pdf', { params, responseType: 'blob' }),
  jobsCSV: (params = {}) => api.get('/exports/jobs/csv', { params, responseType: 'blob' }),
  jobsExcel: (params = {}) => api.get('/exports/jobs/excel', { params, responseType: 'blob' }),
  jobsPDF: (params = {}) => api.get('/exports/jobs/pdf', { params, responseType: 'blob' }),
  timesheetsCSV: (params = {}) => api.get('/exports/timesheets/csv', { params, responseType: 'blob' }),
  attendanceCSV: (params = {}) => api.get('/exports/attendance/csv', { params, responseType: 'blob' }),
  aiMatchesExcel: (jobId) => api.get(`/exports/ai-matches/excel?job_id=${jobId}`, { responseType: 'blob' }),
};

// Calendar API
export const calendarAPI = {
  status: () => api.get('/calendar/status'),
  connect: () => {
    // Pass current origin to backend for dynamic redirect URI
    const redirectUri = `${window.location.origin}/api/calendar/callback`;
    return api.get('/calendar/connect', { params: { redirect_uri: redirectUri } });
  },
  disconnect: () => api.post('/calendar/disconnect'),
  createEvent: (data) => api.post('/calendar/events', data),
  updateEvent: (eventId, data) => api.put(`/calendar/events/${eventId}`, data),
  deleteEvent: (eventId) => api.delete(`/calendar/events/${eventId}`),
  listEvents: (params = {}) => api.get('/calendar/events', { params }),
  sync: () => api.post('/calendar/sync'),
  scheduleInterview: (interviewId) => api.post(`/calendar/schedule-interview/${interviewId}`),
};

// Email API
export const emailAPI = {
  sendInterview: (data) => api.post('/email/send-interview', data),
  sendTemplate: (templateName, data) => api.post('/email/send-template', { template: templateName, ...data }),
  logs: (limit = 20) => api.get('/gmail/logs', { params: { limit } }),
};

// Security Setup API (Admin only)
export const securityAPI = {
  // Profiles
  getProfiles: () => api.get('/setup/profiles'),
  getProfile: (id) => api.get(`/setup/profiles/${id}`),
  createProfile: (data) => api.post('/setup/profiles', data),
  updateProfile: (id, data) => api.put(`/setup/profiles/${id}`, data),
  deleteProfile: (id) => api.delete(`/setup/profiles/${id}`),
  
  // Permission Sets
  getPermissionSets: () => api.get('/setup/permission-sets'),
  getPermissionSet: (id) => api.get(`/setup/permission-sets/${id}`),
  createPermissionSet: (data) => api.post('/setup/permission-sets', data),
  updatePermissionSet: (id, data) => api.put(`/setup/permission-sets/${id}`, data),
  deletePermissionSet: (id) => api.delete(`/setup/permission-sets/${id}`),
  
  // Role Hierarchy
  getRoleHierarchy: () => api.get('/setup/role-hierarchy'),
  createRoleHierarchy: (data) => api.post('/setup/role-hierarchy', data),
  updateRoleHierarchy: (id, data) => api.put(`/setup/role-hierarchy/${id}`, data),
  deleteRoleHierarchy: (id) => api.delete(`/setup/role-hierarchy/${id}`),
  
  // User Security Assignment
  getUsersWithSecurity: (params = {}) => api.get('/setup/users', { params }),
  assignUserSecurity: (userId, data) => api.put(`/setup/users/${userId}/security`, data),
  
  // Get current user permissions
  getMyPermissions: () => api.get('/me/permissions'),
  
  // Team Lead → Recruiter Assignments (Admin only)
  getTeamAssignments: () => api.get('/team-assignments'),
  assignRecruitersToTeamLead: (teamLeadId, recruiterIds) => api.post('/team-assignments/assign', { 
    team_lead_id: teamLeadId, 
    recruiter_ids: recruiterIds 
  }),
  getTeamLeadRecruiters: (teamLeadId) => api.get(`/team-assignments/${teamLeadId}/recruiters`),
  
  // Get my team lead (for recruiters)
  getMyTeamLead: () => api.get('/my-team-lead'),
};

// Team Hierarchy Management (Admin)
export const teamHierarchyAPI = {
  getFull: () => api.get('/admin/team-hierarchy/full'),
  createTeam: (data) => api.post('/admin/team-hierarchy/teams', data),
  updateTeam: (teamId, data) => api.put(`/admin/team-hierarchy/teams/${teamId}`, data),
  deleteTeam: (teamId) => api.delete(`/admin/team-hierarchy/teams/${teamId}`),
  addBDE: (teamId, userId) => api.post('/admin/team-hierarchy/teams/add-bde', { team_id: teamId, user_id: userId }),
  removeBDE: (teamId, userId) => api.post('/admin/team-hierarchy/teams/remove-bde', { team_id: teamId, user_id: userId }),
  addRecruiter: (teamId, userId) => api.post('/admin/team-hierarchy/teams/add-recruiter', { team_id: teamId, user_id: userId }),
  removeRecruiter: (teamId, userId) => api.post('/admin/team-hierarchy/teams/remove-recruiter', { team_id: teamId, user_id: userId }),
  setTeamLead: (teamId, userId) => api.post('/admin/team-hierarchy/teams/set-team-lead', { team_id: teamId, user_id: userId }),
  removeTeamLead: (teamId, userId) => api.post('/admin/team-hierarchy/teams/remove-team-lead', { team_id: teamId, user_id: userId }),
  // iter218 — ICF users + per-node recruiter hierarchy
  setIcfUser: (teamId, userId) => api.post('/admin/team-hierarchy/teams/set-icf-user', { team_id: teamId, user_id: userId }),
  setLeadsgenUser: (teamId, userId) => api.post('/admin/team-hierarchy/teams/set-leadsgen-user', { team_id: teamId, user_id: userId }),
  removeLeadsgenUser: (teamId, userId) => api.post('/admin/team-hierarchy/teams/remove-leadsgen-user', { team_id: teamId, user_id: userId }),
  removeIcfUser: (teamId, userId) => api.post('/admin/team-hierarchy/teams/remove-icf-user', { team_id: teamId, user_id: userId }),
  setNodeRecruiters: (teamId, nodeId, recruiterIds) =>
    api.post('/admin/team-hierarchy/teams/set-node-recruiters', { team_id: teamId, node_id: nodeId, recruiter_ids: recruiterIds }),
  setManager: (teamId, userId) => api.post('/admin/team-hierarchy/teams/set-manager', { team_id: teamId, user_id: userId }),
  getAuditLog: (limit = 50) => api.get('/admin/team-hierarchy/audit-log', { params: { limit } }),
  getPerformanceOverview: (params = {}) => api.get('/admin/team-hierarchy/performance/overview', { params }),
  getJobPerformance: (jobId) => api.get(`/admin/team-hierarchy/performance/job/${jobId}`),
};

// Sidebar Counts API
export const sidebarAPI = {
  getCounts: () => api.get('/sidebar/counts'),
  markViewed: (module) => api.post(`/sidebar/mark-viewed?module=${module}`),
};

// Reports API
export const reportsAPI = {
  // Report config
  getConfig: () => api.get('/reports/config'),
  
  // CRUD operations
  list: (includeDashboard = false) => api.get('/reports', { params: { include_dashboard: includeDashboard } }),
  get: (id) => api.get(`/reports/${id}`),
  create: (data) => api.post('/reports', data),
  update: (id, data) => api.put(`/reports/${id}`, data),
  delete: (id) => api.delete(`/reports/${id}`),
  duplicate: (id) => api.post(`/reports/${id}/duplicate`),
  
  // Dashboard widgets
  getDashboardWidgets: () => api.get('/reports/dashboard'),
  
  // Run and export
  run: (id, params = {}) => api.get(`/reports/${id}/run`, { params }),
  export: (id, format = 'csv', params = {}) => api.get(`/reports/${id}/export`, { 
    params: { format, ...params },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),
  
  // Quick stats
  getQuickStats: () => api.get('/reports/quick-stats'),
};

// Vector Search API - Semantic Search across all entities
export const searchAPI = {
  // Perform semantic search (iter215: backend paginates over the FULL ranked pool)
  semantic: (query, options = {}) => api.post('/search/semantic', {
    query,
    entity_types: options.entityTypes || ['candidates', 'jobs', 'clients'],
    page: options.page || 1,
    page_size: options.pageSize || 20,
    min_score: options.minScore || 0.5
  }),
  
  // Quick search for autocomplete
  quick: (query, types = 'candidates,jobs,clients', limit = 10) => 
    api.get('/search/quick', { params: { q: query, types, limit } }),
  
  // Get index statistics
  stats: () => api.get('/search/stats'),
  
  // Admin: Trigger reindex
  reindex: (entityType = null) => api.post('/search/reindex', null, { 
    params: entityType ? { entity_type: entityType } : {} 
  }),
  
  // Index a single entity
  indexEntity: (entityType, entityId) => api.post(`/search/index/${entityType}/${entityId}`),
  
  // Delete from index
  deleteFromIndex: (entityType, entityId) => api.delete(`/search/index/${entityType}/${entityId}`),
  
  // AI Matching: Get matching candidates for a job
  matchCandidatesForJob: (jobId, options = {}) => api.get(`/search/match/job/${jobId}/candidates`, {
    params: {
      limit: options.limit || 20,
      min_score: options.minScore || 0.3,
      exclude_applied: options.excludeApplied !== false
    }
  }),
  
  // AI Matching: Get matching jobs for a candidate
  matchJobsForCandidate: (candidateId, options = {}) => api.get(`/search/match/candidate/${candidateId}/jobs`, {
    params: {
      limit: options.limit || 10,
      min_score: options.minScore || 0.3
    }
  }),
};

// Gamification API
export const gamificationAPI = {
  // User endpoints
  getDashboard: () => api.get('/gamification/dashboard'),
  getStats: () => api.get('/gamification/stats'),
  getWallet: () => api.get('/gamification/wallet'),
  getEvents: (params = {}) => api.get('/gamification/events', { params }),
  getBadges: () => api.get('/gamification/badges'),
  getPtxBalance: () => api.get('/gamification/ptx'),
  purchaseBadge: (badgeId) => api.post(`/gamification/badges/${badgeId}/purchase`),
  
  // Leaderboard
  getLeaderboard: (period = 'weekly') => api.get(`/gamification/leaderboard/${period}`),
  
  // Admin endpoints
  getAdminConfig: () => api.get('/gamification/admin/config'),
  updateAdminConfig: (data) => api.put('/gamification/admin/config', data),
  getAdminStats: () => api.get('/gamification/admin/stats'),
  processPayouts: () => api.post('/gamification/admin/process-payouts'),
};

// Custom Entities API
export const customEntitiesAPI = {
  // Entity definitions
  list: (includeInactive = false) => api.get('/custom-entities/', { params: { include_inactive: includeInactive } }),
  get: (entityId) => api.get(`/custom-entities/${entityId}`),
  getByName: (entityName) => api.get(`/custom-entities/by-name/${entityName}`),
  create: (data) => api.post('/custom-entities/', data),
  update: (entityId, data) => api.put(`/custom-entities/${entityId}`, data),
  delete: (entityId, deleteData = false) => api.delete(`/custom-entities/${entityId}`, { params: { delete_data: deleteData } }),
  
  // Field management
  addField: (entityId, field) => api.post(`/custom-entities/${entityId}/fields`, field),
  updateField: (entityId, fieldId, field) => api.put(`/custom-entities/${entityId}/fields/${fieldId}`, field),
  deleteField: (entityId, fieldId) => api.delete(`/custom-entities/${entityId}/fields/${fieldId}`),
  
  // Records
  listRecords: (entityName, params = {}) => api.get(`/custom-entities/${entityName}/records`, { params }),
  getRecord: (entityName, recordId) => api.get(`/custom-entities/${entityName}/records/${recordId}`),
  createRecord: (entityName, data) => api.post(`/custom-entities/${entityName}/records`, { data }),
  updateRecord: (entityName, recordId, data) => api.put(`/custom-entities/${entityName}/records/${recordId}`, { data }),
  deleteRecord: (entityName, recordId) => api.delete(`/custom-entities/${entityName}/records/${recordId}`),
  
  // Sidebar items
  getSidebarItems: () => api.get('/custom-entities/sidebar/items'),
  
  // Config
  getIcons: () => api.get('/custom-entities/config/icons'),
  getFieldTypes: () => api.get('/custom-entities/config/field-types'),
};

// iter 181 — Admin global email notifications toggle
export const emailControlAPI = {
  get: () => api.get('/admin/settings/email-notifications'),
  set: (enabled) => api.put('/admin/settings/email-notifications', { enabled }),
};

// iter 181 — Admin Analysis page (iter 182: summary + reassigned jobs)
export const adminAnalysisAPI = {
  summary: (params) => api.get('/admin/analysis/summary', { params }),
  bdePerformance: (params) => api.get('/admin/analysis/bde-performance', { params }),
  teamActivity: (params) => api.get('/admin/analysis/team-activity', { params }),
  recruiterWorkload: (params) => api.get('/admin/analysis/recruiter-workload', { params }),
  reassignedJobs: (params) => api.get('/admin/analysis/reassigned-jobs', { params }),
  qualitySubmissions: (params) => api.get('/admin/analysis/quality-submissions', { params }),
};

// iter 210 — Inhouse Developers matching (merged into the iter 211 inhouseAPI above)

// iter218 — ICF Team portal (hierarchy-scoped)
export const icfTeamAPI = {
  overview: () => api.get('/icf-team/overview'),
  recruiters: () => api.get('/icf-team/recruiters'),
  jobs: (params) => api.get('/icf-team/jobs', { params }),
  assignRecruiters: (jobId, recruiterIds) =>
    api.post(`/icf-team/jobs/${jobId}/assign-recruiters`, { recruiter_ids: recruiterIds }),
  candidates: (params) => api.get('/icf-team/candidates', { params }),
  pipeline: (params) => api.get('/icf-team/pipeline', { params }),
};

export const turingAPI = {
  statusConfig: () => api.get('/turing/status-config'),
  updateStatusConfig: (config, renames) => api.put('/turing/status-config', { config, renames: renames || [] }),
  misKpis: () => api.get('/turing/mis/kpis'),
  submissions: (params) => api.get('/turing/submissions', { params }),
  approve: (candidateId) => api.post(`/turing/submissions/${candidateId}/approve`),
  setPortalStatus: (candidateId, status) => api.patch(`/turing/submissions/${candidateId}/portal-status`, { status }),
  microKpis: () => api.get('/turing/micro/kpis'),
  microSubmissions: (params) => api.get('/turing/micro/submissions', { params }),
  setMicroStatus: (candidateId, status) => api.patch(`/turing/micro/${candidateId}/status`, { status }),
  microTeamKpis: () => api.get('/turing/micro/team/kpis'),
  microTeamSubmissions: (params) => api.get('/turing/micro/team/submissions', { params }),
  teamKpis: () => api.get('/turing/team/kpis'),
  teamPerformance: () => api.get('/turing/team/performance'),
  teamRecruiterPerf: (params) => api.get('/turing/team/recruiter-performance', { params }),
  teamSubmissions: (params) => api.get('/turing/team/submissions', { params }),
  icfKpis: () => api.get('/turing/icf/kpis'),
  icfSheet: (params) => api.get('/turing/icf', { params }),
  icfSpocs: () => api.get('/turing/icf/spocs'),
  icfFilterOptions: () => api.get('/turing/icf/filter-options'),
  icfDelete: (payload) => api.post('/turing/icf/delete', payload),
  icfFlowSubmit: (candidateId, formData) => api.post(`/turing/icf-flow/${candidateId}/submit`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  icfFlowPending: () => api.get('/turing/icf-flow/pending'),
  requirementPerformance: (params) => api.get('/turing/team/requirement-performance', { params }),
  teamAccessList: () => api.get('/turing/team/access'),
  teamAccessUpdate: (userIds) => api.put('/turing/team/access', { user_ids: userIds }),
  icfFlowVerify: (candidateId) => api.post(`/turing/icf-flow/${candidateId}/verify`),
  icfFlowScreenshotUrl: (candidateId) => `${API_URL}/turing/icf-flow/${candidateId}/screenshot?token=${encodeURIComponent(localStorage.getItem('rf_token') || '')}`,
  icfUpdate: (candidateId, data) => api.patch(`/turing/icf/${candidateId}`, data),
  signature: () => api.get('/turing/signature'),
  saveSignature: (data) => api.put('/turing/signature', data),
  emailTemplate: () => api.get('/turing/email-template'),
  sendInstructions: (data) => api.post('/turing/icf/send-instructions', data),
};

// iter244 — "ICF's" page (recruiter/TL/RM own Turing submissions; admin all + SPOC)
export const icfsAPI = {
  eligibility: () => api.get('/icfs/eligibility'),
  list: (params) => api.get('/icfs', { params }),
  updateStatus: (candidateId, stage, status) => api.patch(`/icfs/${candidateId}/status`, { stage, status }),
  updateResult: (candidateId, status) => api.patch(`/icfs/${candidateId}/result`, { status }),
  addEvidence: (candidateId, formData) => api.post(`/icfs/${candidateId}/evidence`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  evidence: (candidateId) => api.get(`/icfs/${candidateId}/evidence`),
  deleteEvidence: (candidateId, shotId) => api.delete(`/icfs/${candidateId}/evidence/${shotId}`),
};

// iter 205 — Internal Screening role APIs
export const screeningAPI = {
  kpis: () => api.get('/screening/kpis'),
  queue: (params) => api.get('/screening/queue', { params }),
  get: (candidateId) => api.get(`/screening/candidate/${candidateId}`),
  saveEvaluation: (candidateId, data) => api.put(`/screening/candidate/${candidateId}/evaluation`, data),
  remove: (candidateId) => api.delete(`/screening/candidate/${candidateId}`),
};

export default api;

// iter223 — LeadsGen module
export const leadsAPI = {
  list: (params) => api.get('/leads', { params }),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  remove: (id) => api.delete(`/leads/${id}`),
  duplicate: (id) => api.post(`/leads/${id}/duplicate`),
  approve: (id) => api.post(`/leads/${id}/approve`),
  apolloLookup: (linkedinUrl) => api.post('/leads/apollo-lookup', { linkedin_url: linkedinUrl }),
  kpis: (params) => api.get('/leads/kpis', { params }),
  dailyActivity: (params) => api.get('/leads/daily-activity', { params }),
  teamMembers: () => api.get('/leads/team-members'),
};
